import {
  BodyText,
  Card,
  Field,
  FieldError,
  HeaderText,
  Input,
  RadioGroup,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { Controller, type UseFormReturn, useFieldArray } from "react-hook-form";
import { POST_ENCOUNTER_ACTIVITY_LABELS } from "../postEncounterActivities";
import type { VisitClosureFormData, VisitClosureFormInput } from "../schemas";
import {
  BILL_TYPE_OPTIONS,
  PATIENT_TYPE_OPTIONS,
  VISIT_TYPE_OPTIONS,
} from "../visitDurationOptions";

interface VisitDurationStepProps {
  form: UseFormReturn<VisitClosureFormInput, unknown, VisitClosureFormData>;
}

export const VisitDurationStep = ({ form }: VisitDurationStepProps) => {
  const { fields } = useFieldArray({
    control: form.control,
    name: "encounterTimeData.postEncounterActivities",
  });

  // Only show other-clinician time when the backend reported any
  const othersChartingTimeSpent = form.watch(
    "encounterTimeData.othersChartingTimeSpent",
  );

  return (
    <div className="flex flex-col gap-2">
      <HeaderText size="sm" tag="h3">
        Visit Type
      </HeaderText>
      <Card className="p-4 flex w-full flex-col items-start gap-2">
        <Controller
          control={form.control}
          name="encounterTimeData.visitType"
          render={({ field, fieldState }) => {
            return (
              <Field data-invalid={fieldState.invalid}>
                <RadioGroup
                  value={field.value ?? ""}
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.trigger("encounterTimeData");
                  }}
                  orientation="vertical"
                  options={VISIT_TYPE_OPTIONS}
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            );
          }}
        />
      </Card>
      <HeaderText size="sm" tag="h3">
        Patient Type
      </HeaderText>
      <Card className="p-4 flex w-full flex-col items-start">
        <Controller
          control={form.control}
          name="encounterTimeData.patientType"
          render={({ field, fieldState }) => {
            return (
              <Field data-invalid={fieldState.invalid}>
                <RadioGroup
                  value={field.value ?? ""}
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.trigger("encounterTimeData");
                  }}
                  orientation="vertical"
                  options={PATIENT_TYPE_OPTIONS}
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            );
          }}
        />
      </Card>
      <HeaderText size="sm" tag="h3">
        Visit Duration
      </HeaderText>
      <Card className="p-2 px-4 flex w-full flex-col gap-2">
        <div className="flex flex-col">
          <div className="flex w-full flex-row items-center gap-2">
            <BodyText size="sm" textColor="primary" className="flex flex-1">
              Your Total Billable Time for this Visit
            </BodyText>
            <Controller
              control={form.control}
              name="encounterTimeData.chartingTimeSpent"
              render={({ field, fieldState }) => {
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <Input
                      value={field.value}
                      onChange={async (e) => {
                        const value = Number.parseInt(e.target.value, 10) || 0;
                        form.setValue(
                          "encounterTimeData.chartingTimeSpent",
                          value,
                          { shouldValidate: true },
                        );
                        form.trigger("encounterTimeData");
                      }}
                      onBlur={field.onBlur}
                      id="chartingTimeSpent"
                      className={cn(
                        "w-20",
                        fieldState.invalid && "border-error",
                      )}
                      type="number"
                      onWheel={(e) => e.currentTarget.blur()}
                      aria-invalid={fieldState.invalid}
                      min={0}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                );
              }}
            />
            <BodyText size="sm" textColor="primary">
              minutes
            </BodyText>
          </div>
        </div>
        {othersChartingTimeSpent > 0 && (
          <div className="flex w-full flex-row items-center gap-2">
            <BodyText size="sm" textColor="secondary" className="flex flex-1">
              Other billable clinical time
            </BodyText>
            <Controller
              control={form.control}
              name="encounterTimeData.othersChartingTimeSpent"
              render={({ field, fieldState }) => {
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <Input
                      value={field.value}
                      onChange={async (e) => {
                        const value = Number.parseInt(e.target.value, 10) || 0;
                        form.setValue(
                          "encounterTimeData.othersChartingTimeSpent",
                          value,
                          { shouldValidate: true },
                        );
                        form.trigger("encounterTimeData");
                      }}
                      onBlur={field.onBlur}
                      id="othersChartingTimeSpent"
                      className="w-20"
                      type="number"
                      onWheel={(e) => e.currentTarget.blur()}
                      aria-invalid={fieldState.invalid}
                      min={0}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                );
              }}
            />
            <BodyText size="sm" textColor="primary">
              minutes
            </BodyText>
          </div>
        )}
        <div className="flex flex-col">
          <div className="flex w-full flex-row items-center gap-2">
            <BodyText size="sm" textColor="primary" className="flex flex-1">
              A/V Call Time
            </BodyText>
            <Controller
              control={form.control}
              name="encounterTimeData.avCallTimeSpent"
              render={({ field, fieldState }) => {
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <Input
                      value={field.value}
                      onChange={async (e) => {
                        const value = Number.parseInt(e.target.value) || 0;
                        form.setValue(
                          "encounterTimeData.avCallTimeSpent",
                          value,
                          { shouldValidate: true },
                        );
                        form.trigger("encounterTimeData");
                      }}
                      onBlur={field.onBlur}
                      id="avCallTimeSpent"
                      className="w-20"
                      type="number"
                      onWheel={(e) => e.currentTarget.blur()}
                      aria-invalid={fieldState.invalid}
                      min={0}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                );
              }}
            />
            <BodyText size="sm" textColor="primary">
              minutes
            </BodyText>
          </div>
        </div>
      </Card>
      <HeaderText size="sm" tag="h3">
        Bill Type
      </HeaderText>
      <Card className="p-4 flex w-full flex-col items-start">
        <Controller
          control={form.control}
          name="encounterTimeData.billType"
          render={({ field, fieldState }) => {
            return (
              <Field data-invalid={fieldState.invalid}>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  orientation="vertical"
                  options={BILL_TYPE_OPTIONS}
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            );
          }}
        />
      </Card>
      <HeaderText size="sm" tag="h3">
        Post Visit Activities
      </HeaderText>
      <Card className="p-2 px-4 flex w-full flex-col gap-2">
        {fields.map((field, index) => {
          return (
            <div
              key={field.id}
              className="flex w-full flex-row items-center gap-2"
            >
              <BodyText size="sm" textColor="primary" className="flex flex-1">
                {POST_ENCOUNTER_ACTIVITY_LABELS[field.activityType]}
              </BodyText>
              <Controller
                control={form.control}
                name={`encounterTimeData.postEncounterActivities.${index}.time`}
                render={({ field, fieldState }) => {
                  return (
                    <Field data-invalid={fieldState.invalid}>
                      <Input
                        value={field.value}
                        onChange={(e) => {
                          form.setValue(
                            `encounterTimeData.postEncounterActivities.${index}.time`,
                            Number.parseInt(e.target.value) || 0,
                          );
                        }}
                        onBlur={field.onBlur}
                        id={`postEncounterActivity-${index}`}
                        className="w-20"
                        type="number"
                        onWheel={(e) => e.currentTarget.blur()}
                        aria-invalid={fieldState.invalid}
                        min={0}
                      />
                      <FieldError errors={[fieldState.error]} />
                    </Field>
                  );
                }}
              />
              <BodyText size="sm" textColor="primary">
                minutes
              </BodyText>
            </div>
          );
        })}
      </Card>
    </div>
  );
};
